import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FARM_CONFIG } from "../config/farmConfig";
import { renderMapImage } from "./mapImage";

const COLUMNS = [
  { key: "name", label: "Nom" },
  { key: "owner", label: "Propriétaire" },
  { key: "crop_label", label: "Culture" },
  { key: "area", label: "Surface (ha)" },
  { key: "immat", label: "Réf. cadastrale" },
  { key: "commune", label: "Commune" },
  { key: "section", label: "Section" },
  { key: "numero", label: "Numéro" },
  { key: "lieu_dit", label: "Lieu-dit" },
  { key: "lease_type", label: "Convention" },
  { key: "risks", label: "Risques" },
  { key: "notes", label: "Notes" },
];

function rowValues(p) {
  return COLUMNS.map((c) => {
    const v = p[c.key];
    if (v === null || v === undefined) return "";
    return v.toString();
  });
}

export function exportExcel(parcels) {
  const data = parcels.map((p) => {
    const obj = {};
    COLUMNS.forEach((c) => (obj[c.label] = p[c.key] ?? ""));
    return obj;
  });
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Parcelles");
  XLSX.writeFile(wb, "parcelles.xlsx");
}

export function exportPDF(parcels) {
  const doc = new jsPDF({ orientation: "landscape" });
  doc.setFontSize(16);
  doc.setTextColor(26, 71, 49);
  doc.text(`${FARM_CONFIG.name} — Liste des parcelles`, 14, 16);
  doc.setFontSize(10);
  doc.setTextColor(120);
  doc.text(
    `Exporté le ${new Date().toLocaleDateString("fr-FR")} — ${parcels.length} parcelle(s)`,
    14,
    23
  );

  autoTable(doc, {
    startY: 28,
    head: [COLUMNS.map((c) => c.label)],
    body: parcels.map(rowValues),
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [26, 71, 49], textColor: 255 },
    alternateRowStyles: { fillColor: [245, 247, 244] },
  });

  doc.save("parcelles.pdf");
}

function slug(s) {
  return (s || "document")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/gi, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();
}

function refOf(p) {
  return p.immat || [p.section, p.numero].filter(Boolean).join(" ");
}

async function addMapToDoc(doc, parcels, y) {
  const img = await renderMapImage(parcels);
  if (!img) return y;
  doc.addImage(img, "JPEG", 14, y, 182, 121);
  doc.setDrawColor(200);
  doc.rect(14, y, 182, 121);
  return y + 127;
}

function docFooter(doc) {
  const h = doc.internal.pageSize.getHeight();
  doc.setFontSize(8);
  doc.setTextColor(150);
  doc.text("Document confidentiel — usage interne à l'exploitation.", 14, h - 8);
}

// Fiche PDF individuelle : carte + détails de la parcelle.
export async function exportParcelSheet(p) {
  const doc = new jsPDF();
  doc.setFontSize(16);
  doc.setTextColor(26, 71, 49);
  doc.text(`Fiche parcelle — ${p.name}`, 14, 16);
  doc.setFontSize(10);
  doc.setTextColor(120);
  doc.text(`Éditée le ${new Date().toLocaleDateString("fr-FR")}`, 14, 22);

  const y = await addMapToDoc(doc, [p], 28);
  const rows = COLUMNS.map((c) => [
    c.label,
    c.key === "immat" ? refOf(p) : (p[c.key] ?? "").toString(),
  ]).filter(([, v]) => v);

  autoTable(doc, {
    startY: y + 2,
    head: [["Champ", "Valeur"]],
    body: rows,
    styles: { fontSize: 9, cellPadding: 2.5 },
    headStyles: { fillColor: [26, 71, 49], textColor: 255 },
    alternateRowStyles: { fillColor: [245, 247, 244] },
    columnStyles: { 0: { cellWidth: 45, fontStyle: "bold" } },
  });

  const hist = (p.crop_history || []).slice().sort((a, b) => b.year - a.year);
  if (hist.length) {
    const startY = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(12);
    doc.setTextColor(26, 71, 49);
    doc.text("Historique des cultures (rotations)", 14, startY);
    autoTable(doc, {
      startY: startY + 3,
      head: [["Campagne", "Culture"]],
      body: hist.map((h) => [h.year.toString(), h.crop_label || h.crop || ""]),
      styles: { fontSize: 9, cellPadding: 2.5 },
      headStyles: { fillColor: [26, 71, 49], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 247, 244] },
      columnStyles: { 0: { cellWidth: 45, fontStyle: "bold" } },
    });
  }
  docFooter(doc);
  doc.save(`fiche_${slug(p.name)}.pdf`);
}

// Fiche PDF par propriétaire : carte de toutes ses parcelles + tableau récapitulatif.
export async function exportOwnerSheet(owner, allParcels) {
  const list = allParcels.filter((p) => p.source !== "xml" && (p.owner || "") === owner);
  if (!list.length) return;
  const totalHa = Math.round(list.reduce((s, p) => s + (p.area || 0), 0) * 100) / 100;

  const doc = new jsPDF();
  doc.setFontSize(16);
  doc.setTextColor(26, 71, 49);
  doc.text(`Fiche propriétaire — ${owner}`, 14, 16);
  doc.setFontSize(10);
  doc.setTextColor(120);
  doc.text(
    `Éditée le ${new Date().toLocaleDateString("fr-FR")} — ${list.length} parcelle(s) — ${totalHa} ha`,
    14,
    22
  );

  const y = await addMapToDoc(doc, list, 28);

  autoTable(doc, {
    startY: y + 2,
    head: [["Nom", "Culture", "Surface (ha)", "Référence", "Commune", "Notes"]],
    body: list.map((p) => [
      p.name,
      p.crop_label || p.crop || "",
      p.area != null ? p.area.toString() : "",
      refOf(p),
      p.commune || "",
      p.notes || "",
    ]),
    foot: [["Total", "", `${totalHa}`, "", "", ""]],
    styles: { fontSize: 9, cellPadding: 2.5 },
    headStyles: { fillColor: [26, 71, 49], textColor: 255 },
    footStyles: { fillColor: [232, 200, 114], textColor: [60, 45, 10], fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 247, 244] },
  });
  docFooter(doc);
  doc.save(`proprietaire_${slug(owner)}.pdf`);
}

// Génère un modèle Excel vierge avec les bons en-têtes.
export function downloadTemplate() {
  const headers = [
    "Nom", "Propriétaire", "Culture", "Surface (ha)", "Commune",
    "Code INSEE", "Section", "Numéro", "Coordonnées", "Notes",
  ];
  const ws = XLSX.utils.aoa_to_sheet([headers]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Parcelles");
  XLSX.writeFile(wb, "modele_parcelles.xlsx");
}
